'use client'

import React, { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import LineHighChart from './LineHighChart'
import { Currency, DeviceType, TickerSize, TokenWithPrice } from './types'
import { formatNumber } from './utils'

interface TokenCardProps {
  token: TokenWithPrice
  currency?: Currency
  deviceType?: DeviceType
  buyButtonText?: string
  locale?: string
  className?: string
  size?: TickerSize
}

const ChevronIcon: React.FC<{ color?: string }> = ({ color = '#7B849B' }) => (
  <svg
    fill="none"
    height="24"
    viewBox="0 0 24 24"
    width="24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

const TokenCard: React.FC<TokenCardProps> = ({
  token,
  currency = 'USD',
  deviceType = 'desktop',
  buyButtonText = 'Buy',
  locale = 'en',
  className = '',
  size = 'lg',
}) => {
  const isDesktop = deviceType === 'desktop'
  const isUpward = token.priceChangePercentage > 0
  const isSmall = size === 'sm'
  const fallbackUrl = `https://static.crypto.com/token/icons/${token.slug}/color_icon.png`
  const [imgSrc, setImgSrc] = useState<string>(token.icon || fallbackUrl)
  const [imgError, setImgError] = useState(false)
  const [priceFlash, setPriceFlash] = useState<'green' | 'red' | null>(null)
  const prevPriceRef = useRef(token.price)
  const hasTriedFallbackRef = useRef(false)

  const handleImageError = () => {
    if (token.icon && !hasTriedFallbackRef.current) {
      hasTriedFallbackRef.current = true
      setImgSrc(fallbackUrl)
    } else {
      setImgError(true)
    }
  }

  useEffect(() => {
    hasTriedFallbackRef.current = false
    setImgError(false)
    setImgSrc(token.icon || fallbackUrl)
  }, [token.slug, token.icon, fallbackUrl])

  useEffect(() => {
    if (prevPriceRef.current !== token.price) {
      setPriceFlash(token.price > prevPriceRef.current ? 'green' : 'red')
      prevPriceRef.current = token.price

      const timer = setTimeout(() => {
        setPriceFlash(null)
      }, 800)

      return () => clearTimeout(timer)
    }
  }, [token.price])

  // Small card variant
  if (isSmall) {
    return (
      <a
        className={twMerge('block flex-none shrink-0', className)}
        href={`https://crypto.com/${locale}/price/${token.slug}`}
        rel="noopener noreferrer"
        style={{ minWidth: '320px', width: '320px' }}
        target="_blank"
      >
        <div className="group flex h-[120px] w-[320px] items-center gap-4 overflow-hidden rounded-[16px] bg-[#1F283C] p-[12px] hover:bg-[#2A3548]">
          {/* Left section: Token icon and info */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {imgError ? (
              <div className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-[#1199FA]">
                <span className="text-[20px] font-bold text-white">
                  {token.symbol.substring(0, 1).toUpperCase()}
                </span>
              </div>
            ) : (
              <img
                alt={token.name}
                className="h-[48px] w-[48px] shrink-0 rounded-full"
                src={imgSrc}
                onError={handleImageError}
              />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-[20px] font-bold text-[#F7F9FA]">
                {token.symbol}
              </span>
              <span
                className={twMerge(
                  'text-[16px] font-bold text-[#F7F9FA]',
                  priceFlash === 'green'
                    ? 'flash-green'
                    : priceFlash === 'red'
                      ? 'flash-red'
                      : '',
                )}
              >
                ${formatNumber(token.price)}
              </span>
              <span
                className={twMerge(
                  'text-[14px]',
                  isUpward ? 'text-[#00A68C]' : 'text-[#ff5c56]',
                )}
              >
                {isUpward ? '+' : ''}
                {token.priceChangePercentage.toFixed(2)}%
              </span>
            </div>
          </div>

          {/* Right section: Mini chart */}
          <div className="relative h-[60px] w-[120px] max-w-[120px] shrink-0 items-start self-start overflow-hidden pt-2">
            <LineHighChart
              data={token.chartData}
              isDesktop={isDesktop}
              isSmall={true}
              isUpward={isUpward}
            />
          </div>
        </div>
      </a>
    )
  }

  // Big card variant (default)
  const cardWidth = isDesktop ? 410 : 288
  return (
    <a
      className={twMerge('block flex-none shrink-0', className)}
      href={`https://crypto.com/${locale}/price/${token.slug}`}
      rel="noopener noreferrer"
      style={{ minWidth: `${cardWidth}px`, width: `${cardWidth}px` }}
      target="_blank"
    >
      <div
        className={twMerge(
          'group rounded-8 flex flex-col bg-[#0B1426] hover:bg-[#1F283C]',
          isDesktop ? 'rounded-4 h-[430px] w-[410px]' : 'h-[312px] w-[288px]',
        )}
      >
        {/* Header with token info */}
        <div
          className={twMerge(
            'flex gap-2',
            isDesktop
              ? 'gap-[17px] pt-8 pr-8 pb-0 pl-8'
              : 'pt-6 pr-6 pb-0 pl-6',
          )}
        >
          {imgError ? (
            <div
              className={twMerge(
                'flex items-center justify-center rounded-[300px] bg-black',
                isDesktop ? 'h-[50px] w-[50px]' : 'h-10 w-10',
              )}
            >
              <span className="text-[24px] font-bold text-white">
                {token.name.substring(0, 1).toUpperCase()}
              </span>
            </div>
          ) : (
            <img
              alt={token.name}
              className={twMerge(
                'rounded-[300px]',
                isDesktop ? 'h-[50px] w-[50px]' : 'h-10 w-10',
              )}
              src={imgSrc}
              onError={handleImageError}
            />
          )}
          <div className="flex flex-col">
            <span
              className={twMerge(
                'font-medium text-[#F7F9FA]',
                isDesktop ? 'text-[18px]' : 'text-[16px]',
              )}
            >
              {token.name}
            </span>
            <span
              className={twMerge(
                'text-[#7B849B]',
                isDesktop ? 'text-[14px]' : 'text-[12px]',
              )}
            >
              {token.symbol}
            </span>
          </div>
        </div>

        {/* Chart */}
        <LineHighChart
          data={token.chartData}
          isDesktop={isDesktop}
          isUpward={isUpward}
        />

        {/* Price section */}
        <div
          className={twMerge(
            'mt-auto flex items-end justify-between',
            isDesktop ? 'pt-0 pr-7 pb-8 pl-7' : 'pt-0 pr-[18px] pb-6 pl-[18px]',
          )}
        >
          <div className="flex flex-col">
            <div className="flex items-baseline gap-1">
              <span
                className={twMerge(
                  'font-bold text-[#F7F9FA]',
                  isDesktop ? 'text-[32px]' : 'text-[24px]',
                  priceFlash === 'green'
                    ? 'flash-green'
                    : priceFlash === 'red'
                      ? 'flash-red'
                      : '',
                )}
              >
                ${formatNumber(token.price)}
              </span>
              <span
                className={twMerge(
                  'text-[#A0A9BE]',
                  isDesktop ? 'text-[14px]' : 'text-[12px]',
                )}
              >
                {currency}
              </span>
            </div>
            <div className="flex gap-1">
              <span
                className={twMerge(
                  'whitespace-nowrap',
                  isUpward ? 'text-[#00A68C]' : 'text-[#ff5c56]',
                  isDesktop ? 'text-[14px]' : 'text-[12px]',
                )}
              >
                {isUpward ? '+' : ''}
                {token.priceChangePercentage.toFixed(2)}%
              </span>
              <span
                className={twMerge(
                  'whitespace-nowrap text-[#A0A9BE]',
                  isDesktop ? 'text-[14px]' : 'text-[12px]',
                )}
              >
                24H
              </span>
            </div>
          </div>

          {/* Buy button or chevron */}
          {isDesktop ? (
            <span className="rounded-[10px] bg-[#1199FA]/20 p-[12px_24px] text-[12px] font-semibold whitespace-nowrap text-[#92D1FF] transition-colors duration-200 group-hover:!bg-[#1199FA] group-hover:text-white hover:!bg-[#1199FA] hover:text-white">
              {buyButtonText}
            </span>
          ) : (
            <span className="self-center">
              <ChevronIcon color="#7B849B" />
            </span>
          )}
        </div>
      </div>
    </a>
  )
}

export default TokenCard
export type { TokenCardProps }
export {
  type ChartData,
  type Currency,
  type DeviceType,
  type TickerSize,
  type TokenWithPrice,
} from './types'
